import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { MutationOptionsAlone } from 'apollo-angular/types';
import { ListUsersGQL, ListUsersQueryVariables, UpdateJobGQL, UpdateJobInput, UpdateJobMutation, UpdateJobMutationVariables } from 'graphql.generated';
import { catchError, exhaustMap, filter, map, mergeMap, of, switchMap } from 'rxjs';

import { generateMutationContext } from 'src/app/core/auth/session';
import { EMPLOYEE_ROLE_ATTRIBUTE, JobRole } from 'src/app/global.constants';
import { jobToolFeature, selectJob, selectJobFranchiseId } from 'src/app/job/job-tool.reducer';

import { PlusAuthenticationService } from '../../../../core/public-api';

import { JobAgentActions } from './job-agent.actions';

export const loadEmployee = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(JobAgentActions.changeAgentMenuItemClicked),
    concatLatestFrom(() => store.select(selectJob)),
    map(([ { role }, job ]) => job?.users?.find((u) => u.role === role)),
    filter(Boolean),
    map(({ user, role }) => JobAgentActions.employeeLoaded({
      employee: user,
      role: role as JobRole,
    })),
  );
}, { functional: true, dispatch: true });

export const assignAgent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
) => {
    return actions$.pipe(
      ofType(JobAgentActions.employeeSelected),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectJob),
          store.select(selectJobFranchiseId)
        ]),
        map(([ { employee, role }, job, jobFranchiseId ]): {
          vars: UpdateJobMutationVariables;
          opts: MutationOptionsAlone<UpdateJobMutation, UpdateJobMutationVariables>;
          role: JobRole;
        } => {

          const updateJobs: UpdateJobInput = {
            jobId: job.id,
            addUsers: [
              {
                role,
                userId: employee.id,
              },
            ],
            removeUsers: [],
          }

          // Requirement: ticket 7056 review - a job can hold two people in the role (an earlier swap only removed
          // the first), and the backend refuses a swap that would leave two, so every current holder is removed.
          // Removals run before additions, so picking one of them again keeps them.
          for (const { user } of job.users.filter((u) => u.role === role && u.user)) {
            updateJobs.removeUsers.push({
              role,
              userId: user.id,
            });
          }

          return {
            vars: { updateJobs },
            opts: {
              context: generateMutationContext({
                zone: jobFranchiseId,
              }),
            },
            role,
          }
        }),
        exhaustMap(({ vars, opts, role }) => updateJobGQL.mutate(vars, opts).pipe(
          map((res) => JobAgentActions.assignAgentSuccess({
            updatedJob: res.data.updateJobs.jobs[0],
            customSuccessMessage: `New ${role} assigned`
          })),
        )),
        catchError((error) => of(JobAgentActions.assignAgentError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const unassignAgent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
) => {
    return actions$.pipe(
      ofType(JobAgentActions.unassignAgentMenuItemClicked),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectJob),
          store.select(selectJobFranchiseId)
        ]),
        map(([ { role }, job, jobFranchiseId ]): {
          vars: UpdateJobMutationVariables;
          opts: MutationOptionsAlone<UpdateJobMutation, UpdateJobMutationVariables>;
          role: JobRole;
        } => {

          // Every holder, as when assigning: an earlier swap may have left two people in the role.
          const currentAgents = job.users.filter((u) => u.role === role && u.user).map((u) => u.user);

          return {
            vars: {
              updateJobs: [
                {
                  jobId: job.id,
                  removeUsers: currentAgents.map((agent) => ({
                    role,
                    userId: agent.id,
                  })),
                },
              ],
            },
            opts: {
              context: generateMutationContext({
                zone: jobFranchiseId,
              }),
            },
            role,
          }
        }),
        exhaustMap(({ vars, opts, role }) => updateJobGQL.mutate(vars, opts).pipe(
          map((res) => JobAgentActions.unassignAgentSuccess({
            updatedJob: res.data.updateJobs.jobs[0],
            customSuccessMessage: `${role} unassigned`
          })),
        )),
        catchError((error) => of(JobAgentActions.unassignAgentError({ error }))),
      )),
    );

}, { functional: true, dispatch: true });

export const searchEmployee = createEffect((
  actions$ = inject(Actions),
	listUsersGQL = inject(ListUsersGQL),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobAgentActions.employeeAutocompleteSearchFired),
      concatLatestFrom(({ role }) => store.select(createSelector(
        jobToolFeature.selectSelectedEmployees,
        (selectedEmployees) => role in selectedEmployees,
      ))),
      filter(([ { query }, hasSelectedEmployee ]) => query.trim().length > 0 && !hasSelectedEmployee),
      map(([ { query } ]): ListUsersQueryVariables => ({
        filter: { search: query, roleHasAttribute: EMPLOYEE_ROLE_ATTRIBUTE },
        skip: 0,
        limit: 25,
      })),
      switchMap((vars) => listUsersGQL.fetch(vars).pipe(
        map((res) => JobAgentActions.searchEmployeesSuccess({ employees: res.data.usersv2.users })),
      )),
    );
}, { functional: true, dispatch: true });

export const selectLoggedInUser = createEffect((
  actions$ = inject(Actions),
  plusAuth = inject(PlusAuthenticationService),
) => {
    return actions$.pipe(
      ofType(JobAgentActions.addMyselfButtonClicked),
      map(({ role }) => JobAgentActions.employeeSelected({
        role,
        employee: {
          id: plusAuth.user.id,
          givenName: plusAuth.user.givenName,
          familyName: plusAuth.user.familyName,
        },
      })),
    );
  }, { functional: true, dispatch: true });
